import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, cld, errMsg, money } from "@/lib/api";
import { ProductForm } from "@/components/admin/ProductForm";
import { TellWaitlistDialog } from "@/components/admin/TellWaitlistDialog";

export const ProductsTab = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [telling, setTelling] = useState(null);

  const load = useCallback(() => api.get("/admin/products").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
  }, [load]);

  const patch = async (p, body) => {
    try {
      await api.patch(`/admin/products/${p.id}`, body);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}" (${p.category})?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Artwork deleted");
    load();
  };

  return (
    <div className="bg-[#FDFBF7] p-6" data-testid="admin-products">
      <div className="mb-6 flex items-center justify-between">
        <p className="eyebrow">{items.length} listings</p>
        <button className="btn-primary px-5 py-3" onClick={() => setEditing({})} data-testid="product-add-btn">Add artwork</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E0D8] text-left text-[10px] uppercase tracking-[0.2em] text-[#5A626A]">
              <th className="py-3 pr-4">Artwork</th><th className="pr-4">Category</th><th className="pr-4">Price</th><th className="pr-4">Formats</th><th className="pr-4">Sold</th><th className="pr-4">Featured</th><th />
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-[#F0EDE6]" data-testid={`product-row-${p.slug}`}>
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-3">
                    <img src={cld(p.images?.[0], "w_96,q_auto,f_auto")} alt="" className="h-12 w-10 bg-[#F2EEE6] object-cover" />
                    <div><p className="font-medium">{p.title}{p.hidden && <span className="ml-2 border border-[#8C7A5B] px-1.5 py-0.5 text-[10px] uppercase tracking-[0.15em] text-[#8C7A5B]" data-testid={`product-hidden-badge-${p.slug}`}>Hidden</span>}</p><p className="text-xs text-[#8A8780]">/{p.slug}</p></div>
                  </div>
                </td>
                <td className="pr-4">{p.category}</td>
                <td className="pr-4">{p.isOriginal ? money(p.price) : `from ${money(p.price)}`}</td>
                <td className="max-w-[200px] pr-4 text-xs text-[#5A626A]">{p.printFormats || "—"}</td>
                <td className="pr-4">{p.isOriginal ? <Switch checked={p.isSold} onCheckedChange={(v) => patch(p, { isSold: v })} data-testid={`product-sold-toggle-${p.slug}`} /> : "—"}</td>
                <td className="pr-4"><Switch checked={!!p.featured} onCheckedChange={(v) => patch(p, { featured: v })} data-testid={`product-featured-toggle-${p.slug}`} /></td>
                <td className="whitespace-nowrap text-right">
                  {p.isOriginal && !p.isSold && <button className="mr-4 text-xs underline underline-offset-4" onClick={() => setTelling(p)} data-testid={`product-tell-waitlist-btn-${p.slug}`}>Tell waitlist</button>}
                  <button className="mr-4 text-xs underline underline-offset-4" onClick={() => setEditing(p)} data-testid={`product-edit-btn-${p.slug}`}>Edit</button>
                  <button className="text-xs text-[#8B2E24] underline underline-offset-4" onClick={() => del(p)} data-testid={`product-delete-btn-${p.slug}`}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ProductForm product={editing} onClose={() => setEditing(null)} onSaved={(saved, wasNew) => { setEditing(null); load(); if (wasNew && saved?.isOriginal && !saved.isSold && !saved.hidden) setTelling(saved); }} />
      <TellWaitlistDialog product={telling} onClose={() => setTelling(null)} />
    </div>
  );
};
